import React, { useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { PhoneOutlined, AudioOutlined, AudioMutedOutlined, HolderOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAppel } from './AppelProvider';

/**
 * Deux formes, jamais bloquantes :
 *  - appel entrant : carte en bas à droite, sonnerie, Accepter / Refuser ;
 *  - appel sortant / en cours : barre sobre en bas à droite avec durée, micro,
 *    raccrocher. Elle reste pendant la navigation.
 * La fenêtre se DÉPLACE à la souris (poignée à gauche) : elle ne doit jamais
 * cacher ce que l'agent est en train de lire. Le micro se coupe dès que
 * l'appel est lancé, pas seulement une fois la communication établie.
 */
const V = { vert: '#1a6b3c', rouge: '#b91c1c', texte: '#111827', muet: '#6b7280', bord: '#e5e7eb', blanc: '#fff', fond: '#f3f4f6' };

const Duree = ({ depuis }) => {
  const [s, setS] = useState(0);
  useEffect(() => {
    if (!depuis) return undefined;
    const id = setInterval(() => setS(Math.floor((Date.now() - depuis) / 1000)), 1000);
    return () => clearInterval(id);
  }, [depuis]);
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{mm}:{ss}</span>;
};

const Rond = ({ onClick, fond, titre, children, disabled }) => (
  <button type="button" onClick={onClick} aria-label={titre} title={titre} disabled={disabled}
    style={{
      width: 44, height: 44, borderRadius: '50%', border: 'none', background: fond, color: '#fff', cursor: disabled ? 'default' : 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, opacity: disabled ? 0.5 : 1,
    }}>
    {children}
  </button>
);

export default function FenetreAppel() {
  const { t } = useTranslation('commun');
  const a = useAppel();
  const noeud = useRef(null);
  if (!a || a.etat === 'inactif' || !a.appel) return null;

  const nom = a.appel.sortant ? a.appel.appeleNom : a.appel.appelantNom;
  const entrant = a.etat === 'appel_entrant';
  const libelle = {
    appel_sortant: t('fenetreAppel.appelEnCours'), connexion: t('fenetreAppel.connexion'), en_cours: <Duree depuis={a.debutCommunication} />, termine: a.motifFin || t('fenetreAppel.appelTermine'),
  }[a.etat];

  return (
    <Draggable handle=".cb-appel-poignee" bounds="body" nodeRef={noeud}>
      <div
        ref={noeud}
        role={entrant ? 'alertdialog' : 'status'} aria-live={entrant ? 'assertive' : 'polite'}
        aria-label={entrant ? t('fenetreAppel.appelEntrantDe', { nom }) : t('fenetreAppel.appelAvec', { nom })}
        style={{
          position: 'fixed', right: 24, bottom: 24, zIndex: 2000, width: 340, background: V.blanc, color: V.texte,
          border: `1px solid ${V.bord}`, borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: '12px 16px 12px 8px',
          fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8,
        }}
      >
        <span className="cb-appel-poignee" title={t('fenetreAppel.deplacer')} aria-hidden="true"
          style={{ cursor: 'grab', color: V.muet, display: 'flex', alignItems: 'center', alignSelf: 'stretch', padding: '0 4px', touchAction: 'none' }}>
          <HolderOutlined />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          {entrant && <div style={{ fontSize: 13, color: V.muet }}>{t('fenetreAppel.appelEntrant')}</div>}
          <div style={{ fontSize: entrant ? 17 : 15, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nom}</div>
          {!entrant && <div style={{ fontSize: 13, color: a.etat === 'termine' ? V.rouge : V.muet }}>{libelle}</div>}
        </div>

        {entrant && (
          <>
            <Rond onClick={() => a.raccrocher('REFUSE')} fond={V.rouge} titre={t('fenetreAppel.refuser')}>
              <PhoneOutlined style={{ transform: 'rotate(135deg)' }} />
            </Rond>
            <Rond onClick={a.accepter} fond={V.vert} titre={t('fenetreAppel.accepter')}>
              <PhoneOutlined />
            </Rond>
          </>
        )}
        {!entrant && a.etat !== 'termine' && (
          <>
            <Rond onClick={a.basculerMicro} fond={a.micCoupe ? V.rouge : V.fond} titre={a.micCoupe ? t('fenetreAppel.reactiverMicro') : t('fenetreAppel.couperMicro')}>
              {a.micCoupe ? <AudioMutedOutlined /> : <AudioOutlined style={{ color: V.texte }} />}
            </Rond>
            <Rond onClick={() => a.raccrocher('RACCROCHE')} fond={V.rouge} titre={t('fenetreAppel.raccrocher')}>
              <PhoneOutlined style={{ transform: 'rotate(135deg)' }} />
            </Rond>
          </>
        )}
      </div>
    </Draggable>
  );
}
